<template>
    <aside role="complementary" class="nav">
        <div
            class="nav__homepage"
            :class="{ hidden: adaptStateToDevice() || !closeMenu }"
        >
            <MobileHomePage :amount="amount" :class="{ hidden: !closeMenu }" />
            <half-payment-options
                :options="options"
                :class="{ hidden: showHalfMenu === false }"
                @show-all-menu="showAllMenu = true"
                v-bind="$attrs"
                @toggle-view-payments="changeMenu"
                :current-payment-option="currentPaymentOption"
            />
            <all-payment-options
                :options="options"
                v-bind="$attrs"
                @minimize="showHalfMenu = true; showAllMenu = false "
                @toggle-view-payments="changeMenu"
                :class="{ hidden: showAllMenu === false }"
                :current-payment-option="currentPaymentOption"
            />
        </div>
        <mobile-nav-menu
            :class="{ hidden: showNavMenu === false }"
            @change-method="displayMobileMenu(e)"
        />
        <div :class="{ hidden: !adaptStateToDevice() }">
            <div class="close-btn-wrapper">
                <button
                    type="button"
                    aria-label="Close Checkout"
                    @click="handleCloseIframe"
                    class="web-close-btn"
                    :class="{ hidden: !adaptStateToDevice() }"
                >
                    <svg
                        width="46"
                        height="46"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle
                            cx="23"
                            cy="23"
                            r="22.629"
                            fill="#fff"
                            stroke="#DEDEDE"
                            stroke-width=".742"
                        />
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M21.586 23l-6.293-6.293a1 1 0 011.414-1.414L23 21.586l6.293-6.293a1 1 0 011.414 1.414L24.414 23l6.293 6.293a1 1 0 01-1.414 1.414L23 24.414l-6.293 6.293a1 1 0 01-1.414-1.414L21.586 23z"
                            fill="#637381"
                        />
                    </svg>
                </button>
            </div>
            <span 
                aria-hidden="true" 
                :class="{ hidden: !adaptStateToDevice() }" 
                id="payment-options-heading" 
                class="nav__header"
            >
                {{ $t("ui.nav.option") }}
            </span>
            <div
                role="listbox"
                aria-orientation="vertical"
                aria-labelledby="payment-options-heading"
                aria-roledescription="Select Menu"
            >
                <ul>
                    <NavItem
                        v-for="(option, index) in options.slice(0, minimumPaymentOptionsCount)"
                        :key="index"
                        :title="options.labels[option]"
                        :name="option"
                        @keydown="moveFocusAndActivate($event, index)"
                        v-bind="$attrs"
                        :logo-name="option"
                        :processing="state.processing"
                        :backgroundProcessing="state.backgroundProcessing"
                        :active="currentPaymentOption === option"
                        :index="index"
                    />
                </ul>
                
                <ul v-if="!isNotAdditionalPaymentMethod || !hideItem">
                    <NavItem
                        v-for="(option, index) in options.slice(minimumPaymentOptionsCount)"
                        :key="index + minimumPaymentOptionsCount"
                        :title="options.labels[option]"
                        :name="option"
                        @keydown="moveFocusAndActivate($event, index + minimumPaymentOptionsCount)"
                        v-bind="$attrs"
                        :logo-name="option"
                        :processing="state.processing"
                        :backgroundProcessing="state.backgroundProcessing"
                        :active="currentPaymentOption === option"
                        :index="index + minimumPaymentOptionsCount"
                    />
                </ul>
            </div>
            
            <button
                v-if="isNotAdditionalPaymentMethod && morePaymentOptionsProps !== null"
                class="control-wrapper"
                role="switch"
                aria-label="View all payment options"
                :aria-checked="morePaymentOptionsProps.ariaChecked"
                @click="toggleControl($event)"
                :class="{ hidden: !adaptStateToDevice() }"
            >
                <div
                    :ref="morePaymentOptionsProps.ref"
                    :class="[ 'menu-control', morePaymentOptionsProps.extraClass ]"
                >
                    <p> {{ translate(morePaymentOptionsProps.text, morePaymentOptionsProps.defaultText) }} </p>
                    <svg
                        :class="morePaymentOptionsProps.ref"
                        width="11"
                        height="15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 11 7"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M.338 9.991l.917.92L6.35 5.77 1.25.63l-.912.919L4.526 5.77.338 9.991z"
                            fill="#666"
                        />
                    </svg>
                </div>
            </button>
        </div>
    </aside>
</template>

<script>
import { ref, onBeforeUnmount, onMounted, computed, provide } from "vue";
import { injectStore, mapState } from "redux-vuex";
import { getValueFromPath } from "@/support/helpers";

import HalfPaymentOptions from "./mobile-payments/half-payment-options";
import AllPaymentOptions from "./mobile-payments/all-payment-options";
import NavItem from "./nav-item";
import MobileNavMenu from "./mobile-payments/mobile-nav-menu";
import MobileHomePage from "./mobile-payments/mobile-homepage";
export default {
    name: "navigation",
    components: {
        MobileHomePage,
        MobileNavMenu,
        NavItem,
        HalfPaymentOptions,
        AllPaymentOptions,
    },
    props: {
        options: {
            type: Array,
            required: true,
        },
        currentPaymentOption: {
            type: String,
            required: true,
        },
        amount: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        const store = injectStore();
        const checkoutData = ref(store.getState());
        const subscription = store.subscribe(() => {
            checkoutData.value = store.getState();
        });
        const hideItem = ref(true);
        const closeMenu = ref(false);
        const showAllMenu = ref(false);
        const showNavMenu = ref(true);
        const showHalfMenu = ref(false);

        const paymentItem = ref(HTMLInputElement);
        // eslint-disable-next-line vue/no-setup-props-destructure
        const paymentOptionsCount = props.options.length;
        const minimumPaymentOptionsCount = 5;

        const focus = ref(props.options.indexOf(props.currentPaymentOption) || -1);

        const morePaymentOptionsProps = computed(() => {
            if (paymentOptionsCount > minimumPaymentOptionsCount) {
                return hideItem.value ?
                    {
                        text: "ui.more.option",
                        defaultText: "More payment options",
                        ref: "more",
                        extraClass: "",
                        ariaChecked: false,
                    } : 
                    {
                        text:"ui.less.option",
                        defaultText: "See fewer options",
                        ref: "less",
                        extraClass:  paymentOptionsCount >= 10 ? "control__max" : " ",
                        ariaChecked: true,
                    };
            }
            return null;
        });

        const isNotAdditionalPaymentMethod = computed(() => {
            return  props.options.indexOf(props.currentPaymentOption) < minimumPaymentOptionsCount;
        });

        function toggleControl() {
            hideItem.value = !hideItem.value;
        }
        function displayMobileMenu() {
            closeMenu.value = !closeMenu.value;
            showNavMenu.value = !showNavMenu.value;
            showHalfMenu.value = !showHalfMenu.value;
        }
        function changeMenu() {
            closeMenu.value = false;
            showNavMenu.value ? "" : (showNavMenu.value = true);
            showAllMenu.value = false;
            showHalfMenu.value = false;
        }
        onMounted(()=>{
            if(
                hideItem.value == true && 
                props.options.indexOf(props.currentPaymentOption) >= minimumPaymentOptionsCount
            ) {
                hideItem.value = false;
            }
        });
        onBeforeUnmount(() => {
            subscription();
        });
        function handleCloseIframe(state) {
            store.Control.close(state);
        }
        
        function initializeFocusIndex(index) {
            index = index % paymentOptionsCount;
            focus.value = index >= 0 ? index : -1;
            hideItem.value = index >= 0 ? isNotAdditionalPaymentMethod.value : !isNotAdditionalPaymentMethod.value;
        }

        provide("initializeFocusIndex", initializeFocusIndex);

        function moveFocusAndActivate(event) {
            let currentFocusIndex = focus.value;
            switch (event.keyCode) {
            case 13:
            case 32:
                dispatchEvent(new CustomEvent("on:ActivateFocusedPaymentOption", {
                    bubbles: false,
                    cancelable: false,
                    detail: focus.value,
                }));

                break;

            case 35:
                focus.value = paymentOptionsCount - 1;
                break;

            case 36:
                focus.value = 0;
                break;

            case 37:
            case 38:
                focus.value = (paymentOptionsCount + --focus.value) % paymentOptionsCount;
                break;

            case 39:
            case 40:
                focus.value = ++focus.value % paymentOptionsCount;
                break;
            }
            hideItem.value = focus.value < minimumPaymentOptionsCount && isNotAdditionalPaymentMethod.value;

            if (currentFocusIndex !== focus.value) {
                const $event = new CustomEvent("on:CurrentFocusIndexChange", {
                    bubbles: false,
                    cancelable: false,
                    // Hold on to current focus index for restoration purposes
                    detail: (currentFocusIndex = focus.value),
                });

                focus.value === minimumPaymentOptionsCount || focus.value === paymentOptionsCount - 1
                    ? requestAnimationFrame(function() {
                        dispatchEvent($event);
                        // Restore current focus index
                        focus.value = currentFocusIndex;
                    })
                    : dispatchEvent($event);
            }
        }

        return {
            state: mapState({
                processing: (state) => getValueFromPath(state, "processing") || false,
                backgroundProcessing: (state) => getValueFromPath(state, "backgroundProcessing") || false,
                currency: (state) =>
                    getValueFromPath(state, "currency.current") || null,
            }),
            toggleControl,
            changeMenu,
            hideItem,
            closeMenu,
            showAllMenu,
            showNavMenu,
            showHalfMenu,
            displayMobileMenu,
            handleCloseIframe,
            moveFocusAndActivate,
            focus,
            paymentItem,
            initializeFocusIndex,
            minimumPaymentOptionsCount,
            morePaymentOptionsProps,
            isNotAdditionalPaymentMethod,
        };
    },
};
</script>

<style lang="scss" scoped>
ul {
  margin: 0;
  padding: 0;
  background: transparent;
}
@media screen and (min-width: 768px) {
  ul {
    background: $background-color;
  }
}

.menu-control {
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 1.3rem;
//   line-height: 1.8rem;
  font-weight: $font-medium;
  color: $black-3;

  svg {
    margin-left: 0.7rem;
  }

}
.web-close-btn {
  display: flex;
  border: 1px solid transparent;
  justify-content: flex-end;
  margin-right: 9vw;
  padding: 0;
  margin-bottom: 1rem;
  box-sizing: border-box;
  background: inherit;
  border-radius: 50%;
  cursor: default;
  @media screen and (max-width: $breakpoint-middle - 1 * 1px) {
    display: none;
  }
  &:focus {
      border-color: #FF9B00;
      box-shadow: 0 0 0 0.05em #ffffff, 0 0 0.15em 0.1em #ff9b00;
  }
}
.close-btn-wrapper {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}
.control-wrapper {
  margin-top: 2rem;
  padding-left: 5.1rem;
  background: inherit;
  border: 1px solid transparent;
  box-sizing: border-box;
  display: flex;

  .more {
      transform: rotate(90deg);
      margin-top: 4px;
  }

  .less {
      transform: rotate(270deg);
      margin-bottom: 4px;
  }

    &:focus {
    border-color: #FF9B00;
  }
}
.control__max {
    padding-bottom: 4rem;
}
</style>